import { dataManager } from '../APIEntity';
import { ActionInfo } from '../models';
import { ComputeNode } from './compute-node';

// ---------------------------------------------------------------------------
// Response schema — 1:1 mirror of the `_CamelModel` subclasses in
// flow_sdk/builtin/faas/git_repo.py (same names, camelCase fields). New fields
// land on the backend model first; this file only reflects them.
// ---------------------------------------------------------------------------

export interface GitStatusFile {
  status: string;
  path: string;
  /** True when the change is in the index (porcelain X column). */
  staged: boolean;
  insertions: number | null;
  deletions: number | null;
}

export interface GitStatus {
  error: string | null;
  branch: string | null;
  /** Configured tracking branch; null does not imply the remote branch is absent. */
  upstream: string | null;
  /** Same-name origin branch known locally and available to track. */
  upstreamCandidate?: string | null;
  ahead: number;
  behind: number;
  files: GitStatusFile[];
  /** Set while the tree is stuck mid-conflict — the UI offers Resolve off it. */
  conflict: GitConflict | null;
  /** Fetch URL of the branch's remote (origin without an upstream); credentials stripped. */
  remoteUrl: string | null;
  /** https browser form of `remoteUrl`, null when the remote isn't a web host. */
  remoteWebUrl: string | null;
}

/** What a conflicted tree is stopped in; null with paths = only a pull's autostash clashed. */
export type ConflictOperation = 'rebase' | 'merge' | 'cherry-pick';

/** A working tree left mid-conflict — mirror of `GitConflict` in git_repo.py. */
export interface GitConflict {
  paths: string[];
  operation: ConflictOperation | null;
}

export interface GitFileDiff {
  diff: string;
}

export interface GitFileContent {
  content: string;
}

export interface GitAssetDiff {
  diff: string;
  files: GitStatusFile[];
}

export interface GitRevision {
  hash: string;
  version: number | null;
  message: string;
  date: string;
  author: string;
}

export interface GitRevisionList {
  revisions: GitRevision[];
  /** Current (HEAD) version parsed from the newest revision. */
  version: number | null;
  /** Commits to this file ahead of @{u} (0 when no upstream). */
  unpushed: number;
}

export interface GitRestoreResult {
  ok: boolean;
  message: string;
}

/** Typed publish outcome — mirror of `PushKind` in git_repo.py. */
export type PushKind =
  | 'pushed'
  | 'nothing'
  | 'conflict'
  | 'permission'
  | 'no_remote'
  | 'network'
  | 'no_repo'
  | 'generic';

export interface GitPushResult {
  ok: boolean;
  kind: PushKind;
  branch: string | null;
  message: string;
  /** Set only when `kind === 'conflict'`: the paths git left unmerged. */
  conflicted: string[];
  /** A commit was made by this push. */
  committed: boolean;
  sha: string | null;
  warning: string | null;
}

/** Typed pull outcome — mirror of `PullKind` in git_repo.py. */
export type PullKind =
  | 'pulled'
  | 'picked'
  | 'nothing'
  | 'conflict'
  | 'permission'
  | 'no_remote'
  | 'network'
  | 'no_repo'
  | 'generic';

export interface GitPullResult {
  ok: boolean;
  kind: PullKind;
  branch: string | null;
  message: string;
  /** Set only when `kind === 'conflict'`: the paths git left unmerged. */
  conflicted: string[];
  /** Set after a successful pull refreshed this local project's asset index. */
  projectId?: string | null;
  refreshError?: string | null;
}

/**
 * Thin client-side wrapper for git operations on a specific working directory.
 *
 * The complete subpath-for-method mirror of `GitRepo.dispatch()`
 * (flow_sdk/builtin/faas/git_repo.py) — all logic is backend-only; every
 * method delegates to the ComputeNode `git-ops` action.
 * The `computeNodeId` defaults to `@local` (the local machine).
 *
 * Renamed from `GitRepo` (now an APIEntity) — this class is a workdir-bound
 * ops helper, not a stored entity.
 *
 * @example
 * ```typescript
 * const git = new GitWorkdir('/home/user/my-repo');
 * const status = await git.getStatus();
 * await git.stageFile('README.md');
 * const result = await git.push();
 * ```
 */
export class GitWorkdir {
  constructor(
    public readonly workDir: string,
    public readonly computeNodeId: string = '@local',
  ) {}

  private async _call<T>(subpath: string, params: Record<string, string> = {}): Promise<T> {
    const action = new ActionInfo('git-ops', ComputeNode.type, this.computeNodeId);
    action.subpath = subpath;
    action.queryParameters = { workdir: this.workDir, ...params };
    return dataManager.callAction<void, T>(action);
  }

  private async _post<T>(subpath: string, params: Record<string, string> = {}): Promise<T> {
    const action = new ActionInfo('git-ops', ComputeNode.type, this.computeNodeId, 'POST');
    action.subpath = subpath;
    action.bodyParameters = { workdir: this.workDir, ...params };
    return dataManager.callAction<Record<string, string>, T>(action);
  }

  // ------------------------------------------------------------------
  // Read probes
  // ------------------------------------------------------------------

  /**
   * Working-tree status, or files changed by `revision` against its first parent. Per-file
   * insertions/deletions are filled only with `lineCounts: true` — they cost a
   * worktree-wide numstat pass, so only a surface that shows them asks.
   */
  async getStatus(options: { lineCounts?: boolean; revision?: string } = {}): Promise<GitStatus> {
    return this._call<GitStatus>('status', {
      ...(options.lineCounts ? { lineCounts: 'true' } : {}),
      ...(options.revision ? { revision: options.revision } : {}),
    });
  }

  async setUpstream(branch: string): Promise<GitStatus> {
    return this._post<GitStatus>('set-upstream', { branch });
  }

  /** Repo-relative paths touched by commits ahead of @{u} (empty when no upstream). */
  async unpushedFiles(): Promise<string[]> {
    return (await this._call<{ files: string[] }>('unpushed-files')).files ?? [];
  }

  /** Commits ahead of @{u}, newest first — the list behind `GitStatus.ahead` (empty when no upstream). */
  async unpushedCommits(): Promise<GitRevision[]> {
    return (await this._call<{ commits: GitRevision[] }>('unpushed-commits')).commits ?? [];
  }

  /** Current branch name, or null if detached / not a git repo. */
  async getBranch(): Promise<string | null> {
    return (await this._call<{ branch: string | null }>('branch')).branch;
  }

  /** True if workDir is inside a git repository. */
  async isInit(): Promise<boolean> {
    return (await this._call<{ isInit: boolean }>('is-init')).isInit ?? false;
  }

  /**
   * True if workDir is a *linked* worktree (not the main worktree).
   *
   * Server runs `git rev-parse --git-dir` and checks whether the output
   * contains `.git/worktrees/`.
   */
  async isLinkedWorktree(): Promise<boolean> {
    return (await this._call<{ isLinkedWorktree: boolean }>('is-linked-worktree')).isLinkedWorktree ?? false;
  }

  /** True if the repo has at least one commit (HEAD exists). */
  async hasCommit(): Promise<boolean> {
    return (await this._call<{ hasCommit?: boolean }>('has-commit')).hasCommit ?? false;
  }

  // ------------------------------------------------------------------
  // Diffs / revisions
  // ------------------------------------------------------------------

  /** File diff: pending changes by default, or a commit against its first parent. */
  async fileDiff(file: string, status: string, revision?: string): Promise<GitFileDiff> {
    return this._call<GitFileDiff>('diff', { file, status, ...(revision ? { revision } : {}) });
  }

  /** Unified diff of an asset's pending changes plus its changed-file list. */
  async assetDiff(file: string): Promise<GitAssetDiff> {
    return this._call<GitAssetDiff>('asset-diff', { file });
  }

  /** Commit history for an asset (folder-scoped for folder-backed assets). */
  async fileRevisions(file: string): Promise<GitRevisionList> {
    return this._call<GitRevisionList>('file-revisions', { file });
  }

  /** Unified diff of an asset between a past revision and the working tree. */
  async revisionDiff(file: string, hash: string): Promise<GitFileDiff> {
    return this._call<GitFileDiff>('revision-diff', { file, hash });
  }

  /** Full file content at a revision (`hash` may be `HEAD`). */
  async show(file: string, hash: string): Promise<GitFileContent> {
    return this._call<GitFileContent>('show', { file, hash });
  }

  /** Full file content from the current working tree. */
  async workingFile(file: string): Promise<GitFileContent> {
    return this._call<GitFileContent>('working-file', { file });
  }

  // ------------------------------------------------------------------
  // Working-tree mutations
  // ------------------------------------------------------------------

  /** Initialize a git repository in workDir (idempotent). */
  async init(): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('init');
  }

  /**
   * Greedy "non-tech" publish: stage-all → commit → pull --rebase → push. A
   * conflict — or a tree still stuck in one — answers `kind: 'conflict'`.
   */
  async push(): Promise<GitPushResult> {
    return this._post<GitPushResult>('push');
  }

  /** Plain push: sync and push the commits already made — stages and commits nothing. */
  async pushCommits(): Promise<GitPushResult> {
    return this._post<GitPushResult>('push-commits');
  }

  /**
   * Fetch the upstream's remote, then the status — the one call that refreshes
   * `behind` (`getStatus` never touches the network). A failed fetch still
   * answers, with `behind` as of the previous fetch.
   */
  async fetch(): Promise<GitStatus> {
    return this._post<GitStatus>('fetch');
  }

  /** Bring the upstream's commits in: `pull --rebase --autostash`. */
  async pull(): Promise<GitPullResult> {
    return this._post<GitPullResult>('pull');
  }

  /** Check out an asset at a past revision (working-tree mutation). */
  async restoreFile(file: string, hash: string): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('restore-file', { file, hash });
  }

  /** Undo a file's pending change (status `?` deletes; otherwise restores to HEAD). */
  async discardFile(file: string, status: string): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('discard-file', { file, status });
  }

  /** Stage just this file (`git add -- <file>`). */
  async stageFile(file: string): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('stage-file', { file });
  }

  /** Unstage just this file (`git restore --staged -- <file>`). */
  async unstageFile(file: string): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('unstage-file', { file });
  }

  /** Commit just this file (no push); anything else staged stays out. */
  async commitFile(file: string): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('commit-file', { file });
  }

  /** Commit every pending change (no push). */
  async commitAll(): Promise<GitRestoreResult> {
    return this._post<GitRestoreResult>('commit-all');
  }
}
